import { todayCheckoutSlots } from '@/features/checkout/checkoutSlots';
import { slotPeriod } from '@/features/menu/servicePeriod';
import { modeToPriceType, type ConsumerMode } from '@/features/menu/resolveModePrice';
import { minRequired } from '@/features/menu/optionRequirement';
import { catalogName } from '@/lib/localizeCatalog';
import type { CartLine, Category, Closure, FirstSlotConfig, Menu, OptionGroup, WeeklyHours } from '@/types';

/** Why a cart line can no longer be ordered. Each reason is a HARD blocker:
 *  nothing the customer can change on the checkout page fixes it, so the line
 *  must leave the cart before the order can be sent.
 *
 *  - 'removed' — the product (or its whole category) was deactivated or deleted
 *                in the back-office, so it is gone from the fresh menu.
 *  - 'mode'    — the product (or its category) is switched off for the selected
 *                fulfilment mode (available_emporter / _livraison / _sur_place).
 *  - 'period'  — the product is midi-only / soir-only and NONE of the service
 *                periods still orderable today accepts it. A product that is
 *                merely restricted to one of two remaining periods is NOT
 *                blocked: the slot picker narrows down instead (SC-75).
 *  - 'option'  — an option the customer picked was deactivated (option item,
 *                per-product override, or the whole group), so the line can no
 *                longer be quoted as saved.
 *  - 'optionRules' — the options are all still on offer, but the group's rules
 *                moved under the line: a group turned required (or raised its
 *                min_select), lowered its max_select, or changed an option's
 *                min/max quantity. resource_api's quote rejects the line on any
 *                of these, so the saved combination is dead as it stands.
 */
export type CartLineBlockReason = 'removed' | 'mode' | 'period' | 'option' | 'optionRules';

/** i18n key explaining each reason to the customer. Lives next to the union so
 *  a new reason cannot ship without its message. */
export const BLOCK_MESSAGE_KEYS: Record<CartLineBlockReason, string> = {
  removed: 'cart.blocked.removed',
  mode: 'cart.blocked.mode',
  period: 'cart.blocked.period',
  option: 'cart.blocked.option',
  optionRules: 'cart.blocked.optionRules',
};

export interface BlockedCartLine {
  lineId: string;
  itemId: string;
  /** Product name from the cart snapshot — the fresh menu may no longer have it. */
  name: string;
  reason: CartLineBlockReason;
}

/** Product-level service flags ANDed with their category's, the way BOTH backends
 *  resolve them: resource_api's quote emits
 *  `active_lunch = product.active_lunch and product.category.active_lunch`, and its
 *  SC-495 wire guard rejects on `product.<flag> or category.<flag>` being off.
 *  The client used to read the product flags alone, so a category restricted to
 *  midi kept an "all day" product addable and the customer only hit the wall at
 *  submit.
 */
export interface EffectiveItemFlags {
  id: string;
  active_lunch: boolean;
  active_dinner: boolean;
  available_sur_place: boolean;
  available_emporter: boolean;
  available_livraison: boolean;
}

const FLAG_KEYS = [
  'active_lunch',
  'active_dinner',
  'available_sur_place',
  'available_emporter',
  'available_livraison',
] as const;

export function effectiveItemFlags(menu: Menu | undefined): EffectiveItemFlags[] {
  if (!menu?.items) return [];
  // A menu snapshot can legitimately arrive without its category list (partial
  // fixture, older cached shape). Missing category ⇒ the product's own flags
  // stand alone, which is the same tolerance the render filters apply.
  const byCategory = new Map<string, Category>(
    (menu.categories ?? []).map((c) => [c.id, c]),
  );
  return menu.items.map((item) => {
    const cat = byCategory.get(item.categoryId);
    const flags = { id: item.id } as EffectiveItemFlags;
    for (const key of FLAG_KEYS) {
      // Absent ⇒ available, never blocked: this is resource_api's own
      // `getattr(product, mode_flag, True)` semantic. Only an explicit `false`
      // is a restriction, so an older/partial menu payload can't silently
      // condemn a whole cart.
      flags[key] = item[key] !== false && (cat ? cat[key] !== false : true);
    }
    return flags;
  });
}

/** Service periods a same-day order can still be scheduled into. Empty means the
 *  restaurant itself is done for the day — a cart-wide problem the drawer already
 *  reports as `cart.noSlotsToday`, not a per-line one, so callers pass the empty
 *  set through and no line is flagged 'period'. */
export function remainingPeriodsToday(
  hours: WeeklyHours | null | undefined,
  now: Date,
  prepMinutes: number,
  closures: Closure[],
  tz?: string,
  firstSlot: FirstSlotConfig | null = null,
): Set<'lunch' | 'dinner'> {
  if (!hours) return new Set();
  const slots = todayCheckoutSlots(hours, now, prepMinutes, undefined, closures, tz, firstSlot);
  return new Set(slots.map(slotPeriod));
}

/** Every option uuid the line carries, flattened across its groups. */
function selectedOptionIds(line: CartLine): string[] {
  return line.selectedOptions.flatMap((g) => g.options.map((o) => o.id));
}

/** Option uuids the fresh menu still offers FOR THIS PRODUCT. Read from
 *  `item.optionGroups` (per-product overrides applied), never from the flat
 *  `Menu.optionGroups`, which collapses a shared group to one product's
 *  detail — see SC-93. */
function orderableOptionIds(groups: OptionGroup[]): Set<string> {
  const ids = new Set<string>();
  for (const group of groups) {
    for (const opt of group.options) ids.add(opt.id);
  }
  return ids;
}

/** Selected quantity per group uuid for one line. */
function qtyPerGroup(line: CartLine): Map<string, number> {
  const totals = new Map<string, number>();
  for (const sel of line.selectedOptions) {
    const sum = sel.options.reduce((acc, o) => acc + o.qty, 0);
    totals.set(sel.groupId, (totals.get(sel.groupId) ?? 0) + sum);
  }
  return totals;
}

/** Does the saved selection still satisfy the product's option rules? Mirrors
 *  resource_api's per-line enforcement (group min_select / max_select, per-option
 *  min_quantity / max_quantity) so the customer is told here rather than by a
 *  400 at payment. */
function violatesOptionRules(line: CartLine, groups: OptionGroup[]): boolean {
  const totals = qtyPerGroup(line);
  for (const group of groups) {
    const total = totals.get(group.id) ?? 0;
    if (minRequired(group) > 0 && total < minRequired(group)) return true;
    if (group.max && total > group.max) return true;
  }
  const choiceById = new Map(groups.flatMap((g) => g.options.map((o) => [o.id, o])));
  for (const sel of line.selectedOptions) {
    for (const entry of sel.options) {
      const choice = choiceById.get(entry.id);
      if (!choice) continue; // absent options are the 'option' reason above
      if (choice.minQty && entry.qty < choice.minQty) return true;
      if (choice.maxQty != null && entry.qty > choice.maxQty) return true;
    }
  }
  return false;
}

export interface DiagnoseCartLinesArgs {
  lines: CartLine[];
  /** Fresh menu snapshot. Undefined (still loading / errored) ⇒ no verdict. */
  menu: Menu | undefined;
  /** Fulfilment mode the order will be submitted with. */
  mode: ConsumerMode;
  /** Periods still orderable today — see remainingPeriodsToday. */
  remainingPeriods: Set<'lunch' | 'dinner'>;
}

/**
 * Cart lines the back-office has made un-orderable since they were added.
 *
 * This is the client mirror of the two wire-level guards the order POST runs:
 * resource_api's quote (unknown/inactive product, category off for the mode,
 * inactive option) and sale_api's service-period check. Until now the SPA
 * tolerated every one of these — repriceCartLines, filterSlotsByCartAvailability
 * and the checkout's price sync all `continue` past a line whose product left the
 * menu — so a cart poisoned by an admin edit looked perfectly fine right up to
 * the reject at payment.
 *
 * Pure: the caller decides how to surface the verdict and when to drop the lines.
 * Order of the returned list follows the cart, and each line is reported once
 * under its first matching reason (removed > mode > period > option), so the UI
 * shows one tag per line.
 */
export function diagnoseCartLines({
  lines,
  menu,
  mode,
  remainingPeriods,
}: DiagnoseCartLinesArgs): BlockedCartLine[] {
  if (!menu?.items || lines.length === 0) return [];
  const itemsById = new Map(menu.items.map((i) => [i.id, i]));
  const flagsById = new Map(effectiveItemFlags(menu).map((f) => [f.id, f]));
  const modeFlag = `available_${modeToPriceType(mode)}` as const;

  const blocked: BlockedCartLine[] = [];
  for (const line of lines) {
    const item = itemsById.get(line.itemId);
    const name = catalogName({
      ...line.snapshot,
      translations: item?.translations ?? line.snapshot.translations,
    });
    if (!item) {
      blocked.push({ lineId: line.lineId, itemId: line.itemId, name, reason: 'removed' });
      continue;
    }
    const flags = flagsById.get(line.itemId);
    if (flags && !flags[modeFlag]) {
      blocked.push({ lineId: line.lineId, itemId: line.itemId, name, reason: 'mode' });
      continue;
    }
    // A line is period-blocked only when EVERY period still orderable today
    // refuses it. With both periods left, a midi-only dish just narrows the slot
    // picker (filterSlotsByCartAvailability) — that is a choice, not a wall.
    if (flags && remainingPeriods.size > 0) {
      const fits = [...remainingPeriods].some((p) =>
        p === 'lunch' ? flags.active_lunch : flags.active_dinner,
      );
      if (!fits) {
        blocked.push({ lineId: line.lineId, itemId: line.itemId, name, reason: 'period' });
        continue;
      }
    }
    // A payload without per-product groups (older/partial menu shape) carries no
    // verdict on options — judging it would condemn every line that has any.
    if (!item.optionGroups) continue;
    const orderable = orderableOptionIds(item.optionGroups);
    const dropped = selectedOptionIds(line).some((id) => !orderable.has(id));
    if (dropped) {
      blocked.push({ lineId: line.lineId, itemId: line.itemId, name, reason: 'option' });
      continue;
    }
    if (violatesOptionRules(line, item.optionGroups)) {
      blocked.push({ lineId: line.lineId, itemId: line.itemId, name, reason: 'optionRules' });
    }
  }
  return blocked;
}

/** Distinct product names of the blocked lines, for a single-sentence banner. */
export function blockedLineNames(blocked: BlockedCartLine[]): string[] {
  return [...new Set(blocked.map((b) => b.name))];
}
